// Runtime model navigačního stavu – lineární stack vrstev

import type { LayerDescriptor } from './layerDescriptor';

/** Navigační stav celé aplikace */
export interface NavigationState {
  /** Lineární stack všech vrstev od nejstarší po vrchní */
  readonly layers: readonly LayerDescriptor[];
  /** ID poslední root vrstvy */
  readonly activeRootLayerId: string | null;
  /** ID aktuálně interaktivní vrchní vrstvy */
  readonly activeTopLayerId: string | null;
}

/** Vytvoří prázdný počáteční stav */
export function createInitialNavigationState(): NavigationState {
  return {
    layers: [],
    activeRootLayerId: null,
    activeTopLayerId: null,
  };
}

/** Přepočítá derived hodnoty stavu po mutaci */
export function recomputeNavigationState(layers: readonly LayerDescriptor[]): NavigationState {
  let activeRootLayerId: string | null = null;
  let activeTopLayerId: string | null = null;

  for (const layer of layers) {
    if (layer.presentation === 'root') {
      activeRootLayerId = layer.layerId;
    }
    activeTopLayerId = layer.layerId;
  }

  // Aktivuj vrchní vrstvu, pokud je lazy
  const activatedLayers = activateTopLayer(layers);

  return { layers: activatedLayers, activeRootLayerId, activeTopLayerId };
}

/** Pokud je vrchní vrstva lazy, přepne ji na ready */
function activateTopLayer(layers: readonly LayerDescriptor[]): readonly LayerDescriptor[] {
  if (layers.length === 0) return layers;
  const topLayer = layers[layers.length - 1];
  if (!topLayer || topLayer.status !== 'lazy') return layers;
  const activated = layers.slice();
  activated[layers.length - 1] = { ...topLayer, status: 'ready' };
  return activated;
}

/** Přidá vrstvu na vrchol stacku */
export function pushLayer(state: NavigationState, layer: LayerDescriptor): NavigationState {
  const newLayers = [...state.layers, layer];
  return recomputeNavigationState(newLayers);
}

/** Odebere vrchní vrstvu */
export function popLayer(state: NavigationState): NavigationState {
  if (state.layers.length === 0) return state;
  const newLayers = state.layers.slice(0, -1);
  return recomputeNavigationState(newLayers);
}

/** Odebere všechny vrstvy nad danou vrstvou (včetně dané vrstvy NE) */
export function closeLayersAbove(state: NavigationState, layerId: string): NavigationState {
  const idx = state.layers.findIndex((l) => l.layerId === layerId);
  if (idx === -1) return state;
  const newLayers = state.layers.slice(0, idx + 1);
  return recomputeNavigationState(newLayers);
}

/** Odebere konkrétní vrstvu podle ID */
export function removeLayer(state: NavigationState, layerId: string): NavigationState {
  const newLayers = state.layers.filter((l) => l.layerId !== layerId);
  return recomputeNavigationState(newLayers);
}

/** Nahradí vrchní vrstvu novou vrstvou (pop + push v jednom kroku) */
export function replaceTopLayer(state: NavigationState, layer: LayerDescriptor): NavigationState {
  if (state.layers.length === 0) return pushLayer(state, layer);
  const newLayers = [...state.layers.slice(0, -1), layer];
  return recomputeNavigationState(newLayers);
}

/**
 * Nahradí docked vrstvy ležící NAD danou root vrstvou jednou novou vrstvou.
 * Osamostatněná (floating) okna žijí ve vlastních stacích a zůstávají zachována — proto se nedá
 * použít `closeLayersAbove`, který slicuje podle indexu a zahodil by i je.
 * Nová vrstva se zařadí jako poslední → je vrchní/aktivní.
 */
export function replaceDockedLayersAbove(
  state: NavigationState,
  rootLayerId: string,
  layer: LayerDescriptor
): NavigationState {
  const idx = state.layers.findIndex((l) => l.layerId === rootLayerId);
  if (idx === -1) return pushLayer(state, layer);
  const kept = state.layers.filter((l, i) => i <= idx || l.stackId !== undefined);
  return recomputeNavigationState([...kept, layer]);
}

/**
 * Nahradí PLOCHU (vrstvy bez `stackId`) novou root vrstvou (closeAll plochy + open).
 * Osamostatněná (floating) okna žijí ve vlastních stacích — ta zůstávají zachována.
 * Nová root se zařadí jako poslední → je vrchní/aktivní (`activeTopLayerId`).
 */
export function replaceWithRoot(state: NavigationState, layer: LayerDescriptor): NavigationState {
  const floating = state.layers.filter((l) => l.stackId !== undefined);
  return recomputeNavigationState([...floating, layer]);
}

/** Aktualizuje state parametry konkrétní vrstvy */
export function updateLayerState(
  state: NavigationState,
  layerId: string,
  stateParams: Record<string, unknown>
): NavigationState {
  const newLayers = state.layers.map((l) => (l.layerId === layerId ? { ...l, stateParams } : l));
  return recomputeNavigationState(newLayers);
}

/**
 * Nastaví nebo zruší `stackId` konkrétní vrstvy.
 * `stackId === undefined` vrstvu vrátí do výchozí plochy (klíč se odstraní).
 */
export function setLayerStackId(
  state: NavigationState,
  layerId: string,
  stackId: string | undefined
): NavigationState {
  const newLayers = state.layers.map((l) => {
    if (l.layerId !== layerId) return l;
    const next: LayerDescriptor = { ...l };
    if (stackId === undefined) {
      delete (next as { stackId?: string }).stackId;
    } else {
      (next as { stackId?: string }).stackId = stackId;
    }
    return next;
  });
  return recomputeNavigationState(newLayers);
}
